/**
 * The format rule of a Music24 social post (social connectors spec §3). One rule gives the name
 * of the format that a platform makes from a post. The publishers pick the path with it. The
 * composer shows the label with it. The readers store the same names. The file has no imports,
 * so every project can use it.
 */

export type SocialPostFormatPlatform = 'tiktok' | 'instagram' | 'youtube' | 'facebook' | 'threads' | 'x';

/** No rule gives `carousel` today: a post has one media file. The readers store the name. */
export type SocialPostFormatName = 'reel' | 'short' | 'video' | 'photo' | 'carousel' | 'text' | 'story';

export interface SocialPostMedia {
  kind: 'image' | 'video';
  /** Pixels. `null` when the file has no stored measure. */
  width: number | null;
  /** Pixels. `null` when the file has no stored measure. */
  height: number | null;
  /** `null` when the file has no stored measure. An image has no duration. */
  durationSeconds: number | null;
  /**
   * Bytes, when known (an uploaded video). The format rule does not read it; the video rules of
   * `postVideoRules.ts` do.
   */
  sizeBytes?: number | null;
}

export type SocialPostFormatHint =
  | 'tooLongForReel'
  | 'tooShortForReel'
  | 'notVerticalForReel'
  | 'tooLongForShort'
  | 'notVerticalForShort'
  | 'unknownMeasures';

/** A Facebook Reel has a duration in this range. The two limits are in the range. */
export const FACEBOOK_REEL_SECONDS = { min: 3, max: 90 };

/** A YouTube Short has a duration of this value or less. */
export const YOUTUBE_SHORT_MAX_SECONDS = 180;

interface VideoMeasures {
  width: number;
  height: number;
  durationSeconds: number;
}

/** Gives the three measures of a video, or `null` when one of them is not known. */
function knownMeasures(media: SocialPostMedia): VideoMeasures | null {
  const { width, height, durationSeconds } = media;
  if (width === null || height === null || durationSeconds === null) {
    return null;
  }
  return { width, height, durationSeconds };
}

/** Gives the reason why a Facebook video is not a Reel, or `null` when it is a Reel. */
function facebookReelMiss(media: SocialPostMedia): SocialPostFormatHint | null {
  const measures = knownMeasures(media);
  if (measures === null) {
    return 'unknownMeasures';
  }
  // A Reel is vertical: a square video is not a Reel.
  if (measures.height <= measures.width) {
    return 'notVerticalForReel';
  }
  if (measures.durationSeconds < FACEBOOK_REEL_SECONDS.min) {
    return 'tooShortForReel';
  }
  if (measures.durationSeconds > FACEBOOK_REEL_SECONDS.max) {
    return 'tooLongForReel';
  }
  return null;
}

/** Gives the reason why a YouTube video is not a Short, or `null` when it is a Short. */
function youtubeShortMiss(media: SocialPostMedia): SocialPostFormatHint | null {
  const measures = knownMeasures(media);
  if (measures === null) {
    return 'unknownMeasures';
  }
  // A Short is vertical or square.
  if (measures.height < measures.width) {
    return 'notVerticalForShort';
  }
  if (measures.durationSeconds > YOUTUBE_SHORT_MAX_SECONDS) {
    return 'tooLongForShort';
  }
  return null;
}

/**
 * Gives the name of the format that the platform makes from the post. `null` means that the
 * platform cannot take this post.
 */
export function resolvePostFormat(
  platform: SocialPostFormatPlatform,
  media: SocialPostMedia | null,
  format: 'post' | 'story'
): SocialPostFormatName | null {
  if (format === 'story') {
    const hasStories = platform === 'facebook' || platform === 'instagram';
    return hasStories && media !== null ? 'story' : null;
  }

  switch (platform) {
    case 'instagram':
      if (media === null) {
        return null;
      }
      return media.kind === 'video' ? 'reel' : 'photo';
    case 'facebook':
      if (media === null) {
        return 'text';
      }
      if (media.kind === 'image') {
        return 'photo';
      }
      return facebookReelMiss(media) === null ? 'reel' : 'video';
    case 'youtube':
      if (media === null || media.kind === 'image') {
        return null;
      }
      return youtubeShortMiss(media) === null ? 'short' : 'video';
    case 'tiktok':
      if (media === null) {
        return null;
      }
      return media.kind === 'video' ? 'video' : 'photo';
    case 'threads':
    case 'x':
      if (media === null) {
        return 'text';
      }
      return media.kind === 'video' ? 'video' : 'photo';
    default:
      return null;
  }
}

/**
 * Gives the reason why a video goes out as a normal video and not as a Reel (Facebook) or a
 * Short (YouTube). It gives the first reason in this order: unknown measures, not vertical, too
 * short (Facebook only), too long. It gives `null` for every other result.
 */
export function formatHint(
  platform: SocialPostFormatPlatform,
  media: SocialPostMedia | null,
  format: 'post' | 'story'
): SocialPostFormatHint | null {
  if (format !== 'post' || media === null || media.kind !== 'video') {
    return null;
  }
  if (platform === 'facebook') {
    return facebookReelMiss(media);
  }
  if (platform === 'youtube') {
    return youtubeShortMiss(media);
  }
  return null;
}
